import React, { useCallback, useEffect, useRef, useState } from "react";
import UnsupportedFileFormatModal from "components/UploadValidation/UnsupportedFileFormatModal";
import FileTooLargeModal from "components/UploadValidation/FileTooLargeModal";

type SectionType = "font" | "image" | "logo" | "icon";

interface BrandKitDropZoneProps {
  type: SectionType;
  accept: string;
  onFile: (file: File) => void;
  // When provided, receives the full batch of dropped/selected files at once.
  // Takes precedence over `onFile` — consumers that want per-file handling
  // can omit this. When `multiple` is true and `onFiles` is omitted, files
  // are forwarded one by one via `onFile`.
  onFiles?: (files: File[]) => void;
  // When true, the file input allows multi-select and drops with multiple
  // files are forwarded one by one via `onFile`.
  multiple?: boolean;
  uploading?: boolean;
}

const TITLES: Record<SectionType, string> = {
  font: "Drop your font file here, or select from your files",
  image: "Drop your image here, or select from your files",
  logo: "Drop your logo here, or select from your files",
  icon: "Drop your icon here, or select from your files",
};

const FORMATS: Record<SectionType, string> = {
  font: ".otf, .ttf, .woff, and .woff2 formats are accepted.",
  image: ".png, .jpg, .svg, and .pdf formats are accepted.",
  logo: ".png, .svg, and .pdf formats are accepted.",
  icon: ".png, .svg, and .pdf formats are accepted.",
};

// Per-section allow-list, kept in sync with the user-visible FORMATS
// text above. The HTML <input accept> attribute is broader (e.g.
// `image/*` matches jpg/gif/etc.) but we want to enforce what the UI
// PROMISES the user we accept — anything outside this list triggers
// the UnsupportedFileFormatModal. `.jpeg` is included alongside
// `.jpg` so both spellings of the same format pass the check.
const ALLOWED_EXTENSIONS: Record<SectionType, string[]> = {
  font: [".otf", ".ttf", ".woff", ".woff2"],
  image: [".png", ".jpg", ".jpeg", ".svg", ".pdf"],
  logo: [".png", ".svg", ".pdf"],
  icon: [".png", ".svg", ".pdf"],
};

// User-visible label list rendered inside the unsupported-format
// modal. Mirrors ALLOWED_EXTENSIONS but collapses `.jpg`/`.jpeg` to
// a single `.jpg` so people don't think they're separate formats.
const DISPLAY_EXTENSIONS: Record<SectionType, string[]> = {
  font: [".otf", ".ttf", ".woff", ".woff2"],
  image: [".png", ".jpg", ".svg", ".pdf"],
  logo: [".png", ".svg", ".pdf"],
  icon: [".png", ".svg", ".pdf"],
};

// Maximum upload size enforced before we forward the file to the
// caller's onFile/onFiles handler. Anything larger surfaces the
// FileTooLargeModal and the whole batch is rejected.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

// Lowercase noun used in the FileTooLargeModal's body copy and
// button label, e.g. "Please upload a smaller font. / Upload New
// Font". Keyed by section type so each dropzone gets the right
// wording without consumers having to thread a prop through.
const SUBJECT: Record<SectionType, string> = {
  font: "font",
  image: "image",
  logo: "logo",
  icon: "icon",
};

const getExtension = (filename: string): string => {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
};

const BrandKitDropZone: React.FC<BrandKitDropZoneProps> = ({
  type,
  accept,
  onFile,
  onFiles,
  multiple = false,
  uploading,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [unsupportedOpen, setUnsupportedOpen] = useState(false);
  const [tooLargeOpen, setTooLargeOpen] = useState(false);
  const onFileRef = useRef(onFile);
  onFileRef.current = onFile;
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  // Per-batch validator. Runs format → size checks in order and
  // short-circuits on the first failure, opening the matching modal.
  // Whole batch is rejected on any failure (simpler than partial-
  // accept — user retries with a cleaner batch). Empty batch is
  // treated as valid so accidental empty drops don't surface a modal.
  // Stored in a ref so the empty-deps native-drop effect below reads
  // the latest validator without needing to re-bind listeners.
  const allowedExts = ALLOWED_EXTENSIONS[type];
  const validateBatchRef = useRef<(files: File[]) => boolean>(() => true);
  validateBatchRef.current = (files: File[]): boolean => {
    if (files.length === 0) return true;
    // 1. Format gate — extension must be in the allow-list. We check
    // this first because if the format is wrong, telling the user
    // "too large" would be misleading.
    const allFormatsValid = files.every((f) => allowedExts.includes(getExtension(f.name)));
    if (!allFormatsValid) {
      setUnsupportedOpen(true);
      return false;
    }
    // 2. Size gate — every file ≤ 25MB. Any oversize file rejects
    // the whole batch.
    const allWithinSize = files.every((f) => f.size <= MAX_UPLOAD_BYTES);
    if (!allWithinSize) {
      setTooLargeOpen(true);
      return false;
    }
    return true;
  };

  // Register native event listeners that call stopPropagation to prevent
  // tldraw canvas handlers (registered on document.body) from intercepting drops
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;

    const nativeDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setDragging(true);
    };

    const nativeDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (el.contains(e.relatedTarget as Node)) return; // still inside
      setDragging(false);
    };

    const nativeDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const nativeDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      // Reject the whole batch if any file's extension is outside the
      // section's allow-list. The validator opens the modal as a side
      // effect; consumers' onFile/onFiles never see the bad batch.
      if (!validateBatchRef.current(files)) return;
      if (onFilesRef.current) {
        onFilesRef.current(files);
      } else {
        files.forEach((f) => onFileRef.current(f));
      }
    };

    el.addEventListener("dragover", nativeDragOver);
    el.addEventListener("dragleave", nativeDragLeave);
    el.addEventListener("dragenter", nativeDragEnter);
    el.addEventListener("drop", nativeDrop);

    return () => {
      el.removeEventListener("dragover", nativeDragOver);
      el.removeEventListener("dragleave", nativeDragLeave);
      el.removeEventListener("dragenter", nativeDragEnter);
      el.removeEventListener("drop", nativeDrop);
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      // Always clear the input value at the end so picking the same
      // file twice still fires `change` — moved into a `finally`-
      // style branch below.
      if (files.length > 0) {
        // Same validation as the native drop path. The HTML `accept`
        // filter is a hint not a guarantee (the user can pick "All
        // files" in the dialog), so we re-check here.
        if (!validateBatchRef.current(files)) {
          if (inputRef.current) inputRef.current.value = "";
          return;
        }
        if (onFiles) {
          onFiles(files);
        } else {
          files.forEach((f) => onFile(f));
        }
      }
      if (inputRef.current) inputRef.current.value = "";
    },
    [onFile, onFiles]
  );

  return (
    <div
      ref={dropRef}
      data-brandkit-dropzone
      onClick={() => !uploading && inputRef.current?.click()}
      style={{
        display: "flex",
        padding: "8px 16px",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        alignSelf: "stretch",
        border: `1px dashed ${dragging ? "#4f5b94" : "#1A2547"}`,
        borderRadius: 4,
        cursor: uploading ? "default" : "pointer",
        background: dragging ? "#f8f9ff" : "transparent",
        transition: "background 0.15s, border-color 0.15s",
        opacity: uploading ? 0.6 : 1,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      {/* Cloud upload icon */}
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
        <g clipPath="url(#clip0_bk_dz)">
          <path
            d="M32.25 16.325C31.1167 9.85625 26.0667 5 20 5C15.1833 5 11 8.075 8.91667 12.575C3.9 13.175 0 17.9563 0 23.75C0 29.9562 4.48333 35 10 35H31.6667C36.2667 35 40 30.8 40 25.625C40 20.675 36.5833 16.6625 32.25 16.325ZM23.3333 21.875V29.375H16.6667V21.875H11.6667L20 12.5L28.3333 21.875H23.3333Z"
            fill="#2E3860"
          />
        </g>
        <defs>
          <clipPath id="clip0_bk_dz">
            <rect width="40" height="40" fill="white" />
          </clipPath>
        </defs>
      </svg>

      {/* Title */}
      <p
        style={{
          color: "#0C172C",
          textAlign: "center",
          fontFamily: '"Inter", sans-serif',
          fontSize: 14,
          fontWeight: 600,
          lineHeight: "16px",
          margin: 0,
        }}
      >
        {uploading ? "Uploading..." : TITLES[type]}
      </p>

      {/* Accepted formats */}
      <p
        style={{
          color: "#798498",
          textAlign: "center",
          fontFamily: '"Inter", sans-serif',
          fontSize: 12,
          fontWeight: 400,
          lineHeight: "12px",
          margin: 0,
        }}
      >
        {FORMATS[type]}
      </p>

      {/* Rendered as a sibling so the modal's overlay doesn't sit
          inside the dropzone (which would inherit cursor / hover
          styles). The portal in the modal moves it to body anyway. */}
      <UnsupportedFileFormatModal
        open={unsupportedOpen}
        onClose={() => setUnsupportedOpen(false)}
        acceptedFormats={DISPLAY_EXTENSIONS[type]}
      />
      <FileTooLargeModal open={tooLargeOpen} onClose={() => setTooLargeOpen(false)} subject={SUBJECT[type]} />
    </div>
  );
};

export default BrandKitDropZone;
